import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  Autocomplete,
  Modal,
  Select,
  useLocalStorageAsState,
  useScreenWidth,
} from "@msys/ui";
import { Input as InputIcon } from "@mui/icons-material";
import { IconButton, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { isEqual } from "lodash-es";
import React from "react";
import {
  EntitySearchFilterOperator,
  Props2,
} from "../../../../clients/graphqlTypes.js";
import { FullScreenToggleIconButton } from "../../../commons/button/FullScreenToggleIconButton.js";
import {
  ExpressionModal_DocumentItemFragment,
  useExpressionModalQuery,
} from "./ExpressionModal.generated.js";
import {
  ExpressionInput,
  filterOptionsByLabel,
  getGroupKey,
  getPropToOption,
  getRenderGroup,
  getSortItems,
  type PropertyOption,
  renderOption,
  sortProps,
} from "./ExpressionModal.js";

interface Props<Operator extends EntitySearchFilterOperator> {
  projectId: string | null;
  docId: string;
  itemId: string;
  filterLabel: string;
  operator: Operator;
  allowedSearchFilterOperators: { label: string; value: Operator }[];
  expression: string | undefined;
  handleClose: () => void;
  handleComplete: (
    value: {
      expr: string;
      operator: Operator;
    },
    handleClose: () => void
  ) => Promise<void> | void;
}

export function ProductSearchFilterExpressionModal<
  Operator extends EntitySearchFilterOperator,
>({
  projectId,
  docId,
  itemId,
  filterLabel,
  operator: passedOperator,
  allowedSearchFilterOperators,
  expression: passedExpression = "",
  handleClose,
  handleComplete,
}: Props<Operator>) {
  const { t } = useTranslate(["Global", "QuoteItem"]);

  const { isMaxPhone } = useScreenWidth();
  const [isFullScreen, setIsFullScreen] = useLocalStorageAsState<boolean>(
    `msys-product-search-expression-modal-fullscreen`,
    false,
    true
  );

  const expressionFieldRef = React.useRef<HTMLTextAreaElement>(null);
  const [inputState, setInputState] = React.useState<{
    expression: string;
    cursorIndex: number | null;
  }>({ expression: passedExpression, cursorIndex: null });

  const [selectedVariable, setSelectedVariable] =
    React.useState<PropertyOption | null>(null);

  const [selectedOperator, setSelectedOperator] =
    React.useState<Operator>(passedOperator);

  const client = useApolloClient();
  const query = useExpressionModalQuery({
    client,
    variables: {
      docId,
      projectId,
    },
  });
  const [items, self] = React.useMemo(() => {
    const items =
      getDataOrNull(query.data?.items)?.items?.filter(validItemTypePredicate) ??
      [];
    const self = items.find(i => i.id === itemId);

    return [items, self, []];
  }, [itemId, query.data?.items]);

  const options = React.useMemo(() => {
    return items
      .sort(getSortItems(self))
      .flatMap(item =>
        [...item.props2].sort(sortProps).map(getPropToOption(item))
      );
  }, [items, self]);

  const handleSave = async () => {
    handleComplete(
      { expr: inputState.expression, operator: selectedOperator },
      handleClose
    );
  };

  return (
    <Modal
      title={t("Build your formula", { ns: "QuoteItem" })}
      dialogProps={
        !isMaxPhone && isFullScreen ? { fullScreen: true } : undefined
      }
      headerActions={
        !isMaxPhone ? (
          <FullScreenToggleIconButton
            isFullScreen={isFullScreen}
            setIsFullScreen={setIsFullScreen}
          />
        ) : undefined
      }
      actionButtons={[
        {
          label: t("Cancel", {
            ns: "Global",
          }),
          handleClick: handleClose,
          buttonProps: {
            variant: "text",
          },
        },
        {
          label: t("Save", {
            ns: "Global",
          }),
          handleClick: handleSave,
          buttonProps: {
            loading: query.loading,
            disabled: !inputState.expression.trim(),
          },
        },
      ]}
      handleClose={handleClose}
    >
      <Stack spacing={1}>
        <Typography color={"secondary"}>{filterLabel}</Typography>
        <Select
          label={"Operator"}
          options={allowedSearchFilterOperators}
          value={selectedOperator}
          onChange={value => {
            setSelectedOperator(value as Operator);
          }}
          required
        />
        <ExpressionInput
          inputRef={expressionFieldRef}
          autoFocus
          label={t("Formula", { ns: "QuoteItem" })}
          value={inputState.expression}
          onChange={event =>
            setInputState(state => ({
              ...state,
              expression: event.target.value,
            }))
          }
          onBlur={event =>
            setInputState(state => ({
              ...state,
              cursorIndex: event.target.selectionStart,
            }))
          }
          onFocus={event => {
            setTimeout(() => {
              event.target.setSelectionRange(
                inputState.cursorIndex,
                inputState.cursorIndex
              );
            });
          }}
          required
        />
        <Stack direction={"row"} spacing={1} alignItems={"center"}>
          <Autocomplete<PropertyOption, false>
            inputLabel={t("Variable for formula", {
              ns: "QuoteItem",
            })}
            options={options}
            groupBy={getGroupKey}
            renderGroup={getRenderGroup({
              itemId,
              items,
              self,
              groupTitle: t("Group", { ns: "QuoteItem" }),
            })}
            renderOption={renderOption}
            filterOptions={filterOptionsByLabel}
            value={selectedVariable}
            isOptionEqualToValue={(option, value) => isEqual(option, value)}
            onChange={value => setSelectedVariable(value)}
          />
          <IconButton
            color={"secondary"}
            disabled={!selectedVariable}
            onClick={() => {
              if (!selectedVariable) return;

              setInputState(({ expression, cursorIndex }) => {
                const index = cursorIndex ?? expression.length;
                return {
                  expression: `${expression.substring(0, index)}${
                    selectedVariable.value
                  }${expression.substring(index)}`,
                  cursorIndex:
                    (cursorIndex ?? 0) + selectedVariable.value.length,
                };
              });

              setTimeout(() => {
                expressionFieldRef.current?.focus();
              });
            }}
          >
            <InputIcon />
          </IconButton>
        </Stack>
      </Stack>
    </Modal>
  );
}

const VALID_DOCUMENT_ITEM_TYPES = ["Item" as const];
// const VALID_DOCUMENT_ITEM_TYPES: DocumentItem["__typename"][] = [
//   "RequirementItem",
//   "QuoteItem",
//   "QuoteTemplateItem",
// ];

function validItemTypePredicate(
  item: ExpressionModal_DocumentItemFragment
): item is Extract<ExpressionModal_DocumentItemFragment, { props2: Props2[] }> {
  return VALID_DOCUMENT_ITEM_TYPES.includes(item.__typename);
}
